import { ExternalLink, FileText, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { Section } from '@/components/manager/overview/Section'
import { usePaymentHistory, type PaymentHistoryItem } from '@/api/stripe'

const STATUS_COLORS: Record<string, string> = {
  succeeded:
    'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300',
  paid: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300',
  pending:
    'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300',
  failed: 'bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-300',
  refunded:
    'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
}

interface Props {
  darkMode?: boolean
}

/**
 * Liste des factures / paiements Stripe. Chaque ligne ouvre le receipt
 * hosted Stripe via `receipt_url` (PDF facture officielle).
 */
export function InvoicesTable({ darkMode = false }: Props) {
  const { t, i18n } = useTranslation()
  const { data: payments = [], isPending, isError } = usePaymentHistory()

  return (
    <Section
      title={t('profile.billing.invoicesTitle', 'Factures')}
      icon={<FileText className="h-5 w-5" />}
    >
      {isPending ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
        </div>
      ) : isError ? (
        <p className="text-sm text-red-500 py-4">
          {t(
            'profile.billing.invoicesError',
            'Impossible de charger les factures.',
          )}
        </p>
      ) : payments.length === 0 ? (
        <p
          className={cn(
            'text-sm py-6 text-center',
            darkMode ? 'text-slate-500' : 'text-slate-500',
          )}
        >
          {t('profile.billing.invoicesEmpty', 'Aucune facture pour le moment.')}
        </p>
      ) : (
        <div className="overflow-x-auto -mx-2">
          <table className="w-full text-sm">
            <thead>
              <tr
                className={cn(
                  'text-left',
                  darkMode ? 'text-slate-400' : 'text-slate-500',
                )}
              >
                <th className="font-medium px-2 py-2 text-xs uppercase tracking-wider">
                  {t('profile.billing.invoiceDate', 'Date')}
                </th>
                <th className="font-medium px-2 py-2 text-xs uppercase tracking-wider">
                  {t('profile.billing.invoiceDescription', 'Description')}
                </th>
                <th className="font-medium px-2 py-2 text-xs uppercase tracking-wider">
                  {t('profile.billing.invoiceStatus', 'Statut')}
                </th>
                <th className="font-medium px-2 py-2 text-xs uppercase tracking-wider text-right">
                  {t('profile.billing.invoiceAmount', 'Montant')}
                </th>
                <th className="font-medium px-2 py-2 text-xs uppercase tracking-wider" />
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <InvoiceRow
                  key={p.id}
                  payment={p}
                  locale={i18n.language}
                  darkMode={darkMode}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  )
}

interface InvoiceRowProps {
  payment: PaymentHistoryItem
  locale: string
  darkMode: boolean
}

function InvoiceRow({ payment, locale, darkMode }: InvoiceRowProps) {
  const { t } = useTranslation()
  const statusClass =
    STATUS_COLORS[payment.status] || STATUS_COLORS.refunded
  return (
    <tr
      className={cn(
        'border-t',
        darkMode ? 'border-slate-800' : 'border-slate-100',
      )}
    >
      <td className="px-2 py-3 whitespace-nowrap tabular-nums text-slate-700 dark:text-slate-300">
        {formatDate(payment.created_at, locale)}
      </td>
      <td className="px-2 py-3 text-slate-700 dark:text-slate-300 max-w-[260px] truncate">
        {payment.description || '—'}
      </td>
      <td className="px-2 py-3">
        <span
          className={cn(
            'inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium',
            statusClass,
          )}
        >
          {payment.status_display || payment.status}
        </span>
      </td>
      <td className="px-2 py-3 text-right font-medium tabular-nums text-slate-900 dark:text-white">
        {formatAmount(payment.amount, payment.currency, locale)}
      </td>
      <td className="px-2 py-3 text-right">
        {payment.receipt_url ? (
          <a
            href={payment.receipt_url}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              'inline-flex items-center gap-1 text-xs font-medium transition-colors',
              darkMode
                ? 'text-blue-400 hover:text-blue-300'
                : 'text-blue-600 hover:text-blue-700',
            )}
            title={t('profile.billing.viewInvoice', 'Voir la facture')}
          >
            {t('profile.billing.view', 'Voir')}
            <ExternalLink className="h-3 w-3" />
          </a>
        ) : (
          <span
            className={cn(
              'text-xs',
              darkMode ? 'text-slate-600' : 'text-slate-400',
            )}
          >
            —
          </span>
        )}
      </td>
    </tr>
  )
}

function formatDate(iso: string, locale: string): string {
  try {
    return new Date(iso).toLocaleDateString(
      locale === 'fr' ? 'fr-CA' : 'en-CA',
      { year: 'numeric', month: 'short', day: 'numeric' },
    )
  } catch {
    return iso
  }
}

function formatAmount(amount: number, currency: string, locale: string): string {
  try {
    return new Intl.NumberFormat(locale === 'fr' ? 'fr-CA' : 'en-CA', {
      style: 'currency',
      currency: currency?.toUpperCase() || 'CAD',
    }).format(amount)
  } catch {
    return `${amount} ${currency || ''}`.trim()
  }
}
